import {Box} from '@mui/material';
import Autoplay from 'embla-carousel-autoplay';
import {Carousel} from 'presentation/components';
import {CarrouselTypeEnum} from 'presentation/constants/carrouselTypeEnum';
import {
	AD_SERVER_PRODUCT_NAME,
	PRODUCT_CODE,
	ProductEnum,
} from 'presentation/constants/productEnum';
import {useHostName, usePersonalData} from 'presentation/hooks';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {useGetHealthAppointmentsHistoryQuery} from 'presentation/modules/myAppointments/queries/useGetHealthAppointmentsHistoryQuery';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useGetBannersV2Query} from '../../query/useGetBannersV2Query';
import {BannersLoading} from '../BannersLoading';
import {BANNERS_V2_CONSTANTS, PLACEMENT_QUANTITY, ProductTypeEnum, UserTypeEnum} from './constants';
import {useBannerTracking} from './hooks/useBannerTracking';
import {StyledImage, StyledLink} from './styles';
import {BannerV2Item} from './types';

export function BannersV2() {
	const {userLocation, accountData} = usePersonalData();
	const {isNotredameSaude} = useHostName();

	const {isMobile} = useMediaQueries();
	const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
	const [isVisible, setIsVisible] = useState(false);
	const bannerRef = useRef<HTMLDivElement>(null);

	const {data: appointmentsHistory = []} = useGetHealthAppointmentsHistoryQuery();
	const recentSchedules = appointmentsHistory.slice(0, 5);

	const {beneficiary, accesses} = accountData;

	const getProductNameByCode = (code: number): string => {
		const productEntry = Object.entries(PRODUCT_CODE).find(([_, codes]) =>
			codes.includes(code),
		);

		if (isNotredameSaude) {
			return AD_SERVER_PRODUCT_NAME[ProductEnum.NOTREDAME_SAUDE];
		}

		return productEntry ? AD_SERVER_PRODUCT_NAME[productEntry[0] as ProductEnum] : '';
	};

	const segmentationData = {
		state: userLocation?.nmUf || '',
		city: `${userLocation?.nmUf || ''}/${userLocation?.nmCidade || ''}`,
		gender: beneficiary.flSexoUsuario || '',
		age: String(beneficiary.idadeC || ''),
		audiences: [
			getProductNameByCode(beneficiary.cdEmpresaPlano ?? 0),
			accesses?.ppoProduct ? ProductTypeEnum.PPO : ProductTypeEnum.HMO,
			beneficiary.nmPlano,
			beneficiary.flTitularSemDireito
				? UserTypeEnum.FINANCIAL_RESPONSIBLE
				: beneficiary.tipoUsuarioC,
			beneficiary.dsStatusUsuario,
			beneficiary.dtAdesaoC,
			beneficiary.dtNascimentoUsuarioC,
			beneficiary.tipoPlanoC?.replace('+', ' '),
			...recentSchedules.flatMap(schedule => [
				schedule?.date?.replace(' - ', 'T')?.concat('-3:00'),
				schedule?.specialty?.description,
			]),
		].filter(Boolean),
	};

	const queryParams = {
		context: BANNERS_V2_CONSTANTS.CONTEXT,
		channel: BANNERS_V2_CONSTANTS.CHANNEL,
		placements: {
			[BANNERS_V2_CONSTANTS.PLACEMENT_NAME]: {
				types: [BANNERS_V2_CONSTANTS.PLACEMENT_TYPE],
				quantity: PLACEMENT_QUANTITY,
				size: BANNERS_V2_CONSTANTS.PLACEMENT_SIZE,
			},
		},
		segmentation: [
			{key: BANNERS_V2_CONSTANTS.SEGMENTATION_KEYS.STATE, values: [segmentationData.state]},
			{key: BANNERS_V2_CONSTANTS.SEGMENTATION_KEYS.CITY, values: [segmentationData.city]},
			{key: BANNERS_V2_CONSTANTS.SEGMENTATION_KEYS.GENDER, values: [segmentationData.gender]},
			{key: BANNERS_V2_CONSTANTS.SEGMENTATION_KEYS.AGE, values: [segmentationData.age]},
			{
				key: BANNERS_V2_CONSTANTS.SEGMENTATION_KEYS.AUDIENCES,
				values: segmentationData.audiences,
			},
		],
	};

	const {data: bannersResponse, isError, isLoading} = useGetBannersV2Query(queryParams);

	const banners = bannersResponse?.[BANNERS_V2_CONSTANTS.PLACEMENT_NAME] as
		| BannerV2Item[]
		| undefined;

	const interleavedBanners = useMemo(() => {
		if (!banners || banners.length === 0) {
			return [];
		}

		const institutionalBanners = banners
			.filter(banner => banner.seller_id === BANNERS_V2_CONSTANTS.SELLER_IDS.INSTITUTIONAL)
			.sort((bannerA, bannerB) => bannerA.position - bannerB.position);

		const commercialBanners = banners
			.filter(banner => banner.seller_id === BANNERS_V2_CONSTANTS.SELLER_IDS.COMMERCIAL)
			.sort((bannerA, bannerB) => bannerA.position - bannerB.position);

		const maxLength = Math.max(institutionalBanners.length, commercialBanners.length);

		return Array.from({length: maxLength}, (_, i) => [
			institutionalBanners[i],
			commercialBanners[i],
		])
			.flat()
			.filter(Boolean);
	}, [banners]);

	useEffect(() => {
		if (!banners || banners.length === 0) {
			return undefined;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				setIsVisible(entry.isIntersecting);
			},
			{
				threshold: 0.1,
			},
		);

		const currentElement = bannerRef.current;

		if (currentElement) {
			observer.observe(currentElement);
		}

		return () => {
			if (currentElement) {
				observer.unobserve(currentElement);
			}
		};
	}, [banners]);

	const {handleBannerClick} = useBannerTracking({
		banners,
		currentSlideIndex,
		interleavedBanners,
		isVisible,
	});

	if (isError) {
		return null;
	}

	if (isLoading) {
		return <BannersLoading />;
	}

	const isEmpty = !banners || banners.length === 0;

	if (isEmpty) {
		return null;
	}

	return (
		<Box ref={bannerRef}>
			<Carousel
				hideButtons={isMobile}
				type={CarrouselTypeEnum.DOTS}
				plugins={[Autoplay({stopOnInteraction: false})]}
				isFullscreen
				controlledSelectedIndex={currentSlideIndex}
				setControlledSelectedIndex={setCurrentSlideIndex}
				slides={interleavedBanners.map((banner, index) => (
					<StyledLink
						key={banner.ad_id || index}
						href={banner.destination_url}
						target="_blank"
						rel="noreferrer noopener"
						onClick={() => handleBannerClick(banner.click_url)}>
						<StyledImage src={banner.media_url} alt={`Banner ${index + 1}`} />
					</StyledLink>
				))}
			/>
		</Box>
	);
}
